import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { jsPDF } from 'jspdf';
import { TranslateService } from '@ngx-translate/core';
import { AdminOrderDetail, AdminOrderItem } from './admin-sign-order.service';
import { QrPosterDocumentService } from './qr-poster-document.service';
import { QrPosterRenderAssetLoader } from './qr-poster-render-assets.service';
import { SignOrderCanonicalRenderSnapshotAdapterService } from './sign-order-canonical-render-snapshot-adapter.service';
import { BE_API_AUTH_URL } from '../constants/app.constants';

export class SignOrderPdfError extends Error {
  constructor(
    message: string,
    public readonly propertyIdentifier?: string,
  ) {
    super(message);
    this.name = 'SignOrderPdfError';
  }
}

@Injectable({ providedIn: 'root' })
export class SignOrderPdfService {
  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private canonicalDocumentService: QrPosterDocumentService,
    private canonicalSnapshotAdapter: SignOrderCanonicalRenderSnapshotAdapterService,
  ) {}

  async previewItem(order: AdminOrderDetail, item: AdminOrderItem): Promise<void> {
    const previewWindow = window.open('about:blank', '_blank');
    if (!previewWindow) {
      throw new SignOrderPdfError(
        this.translate.instant('SIGN_ORDER.ADMIN.PDF_PREVIEW_BLOCKED'),
        item.identifier || item.propertyId,
      );
    }

    try {
      const blob = await this.generateItemBlob(order, item);
      const url = URL.createObjectURL(blob);
      previewWindow.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      previewWindow.close();
      throw err;
    }
  }

  async downloadIndividualPdf(order: AdminOrderDetail, item: AdminOrderItem): Promise<void> {
    const blob = await this.generateItemBlob(order, item);
    const safeId = (item.identifier || item.propertyId || 'item').replace(/[^a-zA-Z0-9._-]/g, '_');
    const fileName = `${order.orderNumber}_${safeId}.pdf`;
    this.downloadBlob(blob, fileName);
  }

  async downloadCombinedPdf(order: AdminOrderDetail): Promise<void> {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a5' });
    const items = order.items || [];
    let pageCount = 0;

    for (const item of items) {
      for (let c = 0; c < item.copies; c++) {
        if (pageCount > 0) pdf.addPage();
        pageCount++;
        try {
          await this.renderItemPage(pdf, order, item);
        } catch (err: unknown) {
          const propId = item.identifier || item.address || item.propertyId || `#${c + 1}`;
          const reason = err instanceof Error ? err.message : 'Unknown error';
          throw new SignOrderPdfError(
            `${this.translate.instant('SIGN_ORDER.ADMIN.PDF_COMBINED_PAGE_ERROR')}: ${propId} (${c + 1}/${item.copies}) — ${reason}`,
            propId,
          );
        }
      }
    }

    const totalCopies = items.reduce((sum, i) => sum + i.copies, 0);
    const fileName = `${order.orderNumber}_combined_${totalCopies}.pdf`.replace(/[^a-zA-Z0-9._-]/g, '_');
    pdf.save(fileName);
  }

  private async generateItemBlob(order: AdminOrderDetail, item: AdminOrderItem): Promise<Blob> {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a5' });
    await this.renderItemPage(pdf, order, item);
    return pdf.output('blob');
  }

  private async renderItemPage(pdf: jsPDF, order: AdminOrderDetail, item: AdminOrderItem): Promise<void> {
    await this.renderCanonicalItemPage(pdf, order, item);
  }

  private async renderCanonicalItemPage(pdf: jsPDF, order: AdminOrderDetail, item: AdminOrderItem): Promise<void> {
    try {
      const response = this.canonicalSnapshotAdapter.toDescriptorResponse(item);
      await this.canonicalDocumentService.renderDescriptorToPage(pdf, response, {
        renderSource: 'sign-order-canonical-admin-pdf',
        assetLoader: this.createCanonicalAssetLoader(order),
      });
    } catch (error) {
      throw new SignOrderPdfError(
        this.translate.instant('SIGN_ORDER.ADMIN.PDF_GENERATION_ERROR'),
        item.identifier || item.propertyId,
      );
    }
  }

  private createCanonicalAssetLoader(order: AdminOrderDetail): QrPosterRenderAssetLoader {
    return {
      loadLogo: (descriptor) => this.loadCanonicalLogoBlob(order.publicId, descriptor.property.propertyId),
      loadTemplateImage: (descriptor, assetKey) => this.loadCanonicalTemplateImageBlob(
        order.publicId,
        descriptor.property.propertyId,
        assetKey,
      ),
    };
  }

  private async loadCanonicalLogoBlob(orderId: string, propertyId: string): Promise<Blob | null> {
    const response: HttpResponse<Blob> = await firstValueFrom(this.http.get(
      `${BE_API_AUTH_URL}/admin/sign-orders/${encodeURIComponent(orderId)}/items/${encodeURIComponent(propertyId)}/qr-poster-logo`,
      { observe: 'response', responseType: 'blob' },
    ));
    if (response.status === 204) {
      return null;
    }
    if (!response.body) {
      throw new Error('Canonical QR poster logo response is empty');
    }
    return response.body;
  }

  private async loadCanonicalTemplateImageBlob(
    orderId: string,
    propertyId: string,
    assetKey: string,
  ): Promise<Blob | null> {
    try {
      return await firstValueFrom(this.http.get(
        `${BE_API_AUTH_URL}/admin/sign-orders/${encodeURIComponent(orderId)}/items/${encodeURIComponent(propertyId)}/qr-poster-template-asset`,
        { responseType: 'blob', params: { key: assetKey } },
      ));
    } catch (error) {
      if (this.isNotFound(error)) {
        return null;
      }
      throw error;
    }
  }

  private isNotFound(error: unknown): boolean {
    return typeof error === 'object'
      && error !== null
      && (error as { status?: unknown }).status === 404;
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
